/**
 * Benchmark Performance Thresholds Configuration
 *
 * This file defines the thresholds used across all benchmarking charts to determine
 * performance levels and assign colors based on the ABSOLUTE DIFFERENCE between
 * brand performance and industry average (in percentage points).
 *
 * Example:
 * - Brand Score: 26.3%
 * - Industry Average: 22.8%
 * - Absolute Difference: +3.5 percentage points (26.3 - 22.8)
 * - Performance Level: "Very Good" (falls between 2.0 and 5.0)
 */

export interface PerformanceLevel {
  /** Minimum absolute difference (in percentage points) for this level */
  threshold: number;
  /** Color to use for this performance level */
  color: string;
  /** Background color for tooltips and cards */
  bg: string;
  /** Performance category identifier */
  performance: 'excellent' | 'very-good' | 'good' | 'fair' | 'poor' | 'critical';
  /** Human-readable label for this performance level */
  label: string;
}

/**
 * Performance thresholds based on absolute percentage point difference.
 *
 * These thresholds determine the color coding and performance labels across
 * all benchmarking visualizations. Adjust these values to change the sensitivity
 * of the performance grading system.
 *
 * Default values are calibrated for brand equity scores typically ranging 20-40%.
 */
export const PERFORMANCE_THRESHOLDS: PerformanceLevel[] = [
  {
    threshold: 5.0,
    color: '#16a34a',
    bg: '#dcfce7',
    performance: 'excellent',
    label: 'Outstanding Performance'
  },
  {
    threshold: 2.0,
    color: '#22c55e',
    bg: '#ecfccb',
    performance: 'very-good',
    label: 'Strong Performance'
  },
  {
    threshold: 0.0,
    color: '#4ade80',
    bg: '#f0fdf4',
    performance: 'good',
    label: 'Above Average'
  },
  {
    threshold: -2.0,
    color: '#f59e0b',
    bg: '#fef3c7',
    performance: 'fair',
    label: 'Below Average'
  },
  {
    threshold: -5.0,
    color: '#f97316',
    bg: '#fed7aa',
    performance: 'poor',
    label: 'Needs Improvement'
  },
  {
    threshold: -Infinity,
    color: '#dc2626',
    bg: '#fecaca',
    performance: 'critical',
    label: 'Urgent Attention Required'
  }
];

/**
 * Helper function to get performance colors and labels based on absolute difference.
 *
 * @param absoluteDifference - The difference between brand value and industry mean (brandValue - industryMean)
 * @returns Performance level with color, background, and label information
 *
 * @example
 * ```typescript
 * const brandScore = 26.3;
 * const industryAvg = 22.8;
 * const diff = brandScore - industryAvg; // 3.5
 * const { color, label } = getPerformanceLevel(diff);
 * // Returns: { color: '#22c55e', label: 'Strong Performance', ... }
 * ```
 */
export function getPerformanceLevel(absoluteDifference: number): PerformanceLevel {
  // Find the first threshold that the difference meets or exceeds
  // (thresholds are ordered from highest to lowest)
  const level = PERFORMANCE_THRESHOLDS.find(
    threshold => absoluteDifference >= threshold.threshold
  );

  // This should always find a match due to -Infinity threshold, but provide fallback
  return level || PERFORMANCE_THRESHOLDS[PERFORMANCE_THRESHOLDS.length - 1];
}

/**
 * Calculate the absolute difference between brand value and industry mean.
 *
 * @param brandValue - The brand's performance score (percentage)
 * @param industryMean - The industry average score (percentage)
 * @returns Absolute difference in percentage points
 */
export function calculateAbsoluteDifference(brandValue: number, industryMean: number): number {
  return brandValue - industryMean;
}
